/*
  InvoiceFormDialog.jsx — Create/Edit draft invoice form dialog.
  Extracted from InvoicesPanel.jsx (Feb 2026 refactor for line-count reduction).
*/
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Trash2, Download, QrCode } from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function InvoiceFormDialog({
  open, onOpenChange, editingId, docType, form, setForm, projects, company,
  formProject, formCategory, sameState, formTaxable, formTax,
  emptyLine, updateLine, pullFromBillingLines, submit,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[900px] max-h-[90vh] overflow-y-auto" data-testid="invoice-form-dialog">
        <DialogHeader>
          <DialogTitle>{editingId ? "Edit Draft" : `New ${docType === "proforma" ? "Proforma" : "Tax"} Invoice`}</DialogTitle>
          <DialogDescription className="text-xs">Select a project — client GST + bank info snapshot-locks when you issue.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Project *</Label>
              <select className="w-full h-9 border rounded px-2 text-xs bg-white" value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })} data-testid="form-project">
                <option value="">— Select project —</option>
                {projects.filter(p => p.status === "active" || !editingId).map(p => (
                  <option key={p.id} value={p.id}>{p.name} · {p.client_name || "no client"}</option>
                ))}
              </select>
            </div>
            <div><Label className="text-xs">Issue Date</Label><Input type="date" value={form.issue_date} onChange={e => setForm({ ...form, issue_date: e.target.value })} className="h-9 text-xs" data-testid="form-issue-date" /></div>
            <div><Label className="text-xs">Due Date</Label><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="h-9 text-xs" data-testid="form-due-date" /></div>
            <div className="md:col-span-2">
              <Label className="text-xs">GST Category (override — else project default)</Label>
              <select className="w-full h-9 border rounded px-2 text-xs bg-white" value={form.gst_category_override} onChange={e => setForm({ ...form, gst_category_override: e.target.value })} data-testid="form-gst-category">
                <option value="">Use project default ({formProject?.gst_category || "B2B"})</option>
                <option value="B2B">B2B (18% GST)</option>
                <option value="B2C">B2C (0% GST)</option>
                <option value="Export">Export (0% GST)</option>
              </select>
            </div>
            <div className="md:col-span-2 text-[10.5px] bg-slate-50 border rounded p-2">
              <div className="flex items-center justify-between"><span className="text-slate-500">Client State:</span><span className="font-medium">{formProject?.client_state || "—"} {formProject?.client_state_code ? `(${formProject.client_state_code})` : ""}</span></div>
              <div className="flex items-center justify-between"><span className="text-slate-500">Issuer State Code:</span><span className="font-medium">{company?.state_code || "—"}</span></div>
              <div className="flex items-center justify-between"><span className="text-slate-500">Tax mode:</span><span className="font-medium text-[#0066CC]">{formCategory !== "B2B" ? "No GST" : sameState ? "CGST + SGST (9%+9%)" : "IGST (18%)"}</span></div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold">Line Items</Label>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={pullFromBillingLines} className="h-7 text-xs gap-1" data-testid="pull-billing-lines"><Download className="w-3 h-3" /> Pull from Billing Lines</Button>
                <Button size="sm" variant="outline" onClick={() => setForm(f => ({ ...f, line_items: [...f.line_items, emptyLine()] }))} className="h-7 text-xs gap-1" data-testid="add-line-item"><Plus className="w-3 h-3" /> Add Line</Button>
              </div>
            </div>
            <table className="w-full text-xs border">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase">
                  <th className="p-1.5 text-left">Description *</th>
                  <th className="p-1.5 text-center w-[80px]">HSN/SAC</th>
                  <th className="p-1.5 text-right w-[70px]">Qty</th>
                  <th className="p-1.5 text-right w-[100px]">Rate</th>
                  <th className="p-1.5 text-right w-[110px]">Amount</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {form.line_items.map((li, i) => (
                  <tr key={i} className="border-t">
                    <td className="p-1"><Textarea value={li.description} onChange={e => updateLine(i, { description: e.target.value })} rows={1} className="text-xs min-h-[32px]" data-testid={`line-desc-${i}`} /></td>
                    <td className="p-1"><Input value={li.hsn_sac} onChange={e => updateLine(i, { hsn_sac: e.target.value })} className="h-8 text-xs text-center" data-testid={`line-hsn-${i}`} /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={li.qty} onChange={e => updateLine(i, { qty: e.target.value })} className="h-8 text-xs text-right" data-testid={`line-qty-${i}`} /></td>
                    <td className="p-1"><Input type="number" step="0.01" value={li.rate} onChange={e => updateLine(i, { rate: e.target.value })} className="h-8 text-xs text-right" data-testid={`line-rate-${i}`} /></td>
                    <td className="p-1 text-right font-medium">{fmt(li.qty * li.rate)}</td>
                    <td className="p-1 text-center">
                      <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }))} data-testid={`remove-line-${i}`}><Trash2 className="w-3 h-3" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t bg-slate-50"><td colSpan="4" className="p-2 text-right font-semibold text-slate-600">Taxable Value</td><td className="p-2 text-right font-semibold">₹ {fmt(formTaxable)}</td><td></td></tr>
                {formTax.cgst > 0 && <>
                  <tr><td colSpan="4" className="p-1.5 text-right text-slate-500">SGST (9%)</td><td className="p-1.5 text-right">₹ {fmt(formTax.sgst)}</td><td></td></tr>
                  <tr><td colSpan="4" className="p-1.5 text-right text-slate-500">CGST (9%)</td><td className="p-1.5 text-right">₹ {fmt(formTax.cgst)}</td><td></td></tr>
                </>}
                {formTax.igst > 0 && <tr><td colSpan="4" className="p-1.5 text-right text-slate-500">IGST (18%)</td><td className="p-1.5 text-right">₹ {fmt(formTax.igst)}</td><td></td></tr>}
                <tr className="bg-[#0066CC] text-white"><td colSpan="4" className="p-2 text-right font-bold">Total</td><td className="p-2 text-right font-bold" data-testid="form-total">₹ {fmt(formTax.total)}</td><td></td></tr>
              </tfoot>
            </table>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div><Label className="text-xs">Narration</Label><Textarea value={form.narration} onChange={e => setForm({ ...form, narration: e.target.value })} rows={2} className="text-xs" placeholder="Cloud Service for July, August, September" data-testid="form-narration" /></div>
            <div><Label className="text-xs">Internal Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="text-xs" data-testid="form-notes" /></div>
          </div>

          {docType === "tax" && (
            <div className="border rounded p-3 bg-slate-50 space-y-2">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-700">
                <QrCode className="w-3.5 h-3.5 text-[#0066CC]" /> E-Invoice Details
                <span className="text-[10px] font-normal text-slate-500">(Optional — populate after uploading to GSTN IRP)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">IRN Number <span className="text-slate-400">(64 chars)</span></Label>
                  <Input value={form.irn_number} onChange={e => setForm({ ...form, irn_number: e.target.value })} placeholder="d1e2c3b4a5..." className="h-8 text-xs font-mono" data-testid="form-irn" />
                </div>
                <div>
                  <Label className="text-xs">QR Code Data <span className="text-slate-400">(signed JSON / URL from IRP)</span></Label>
                  <Textarea value={form.qr_code_data} onChange={e => setForm({ ...form, qr_code_data: e.target.value })} rows={2} className="text-xs font-mono" placeholder="eyJhbGciOiJSUzI1NiJ9..." data-testid="form-qr" />
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-draft-btn">Save Draft</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
